import React, { useMemo, useState } from 'react';
import { addMonths, subMonths, format, isSameMonth, isToday, isWeekend } from 'date-fns';
import { getCalendarDays, computePercentileRanks, pnlCellStyle, computeMonthlyStats } from './calendarUtils';
import type { CalendarResponse, CalendarDay } from './calendarUtils';
import DayDetail from './DayDetail';
import CalendarStats from './CalendarStats';
import styles from './TradeCalendar.module.css';

interface TradeCalendarProps {
  data: CalendarResponse | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onReplayTrade?: (decisionSK: string) => void;
}

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TradeCalendar: React.FC<TradeCalendarProps> = ({ data, loading, error, onRetry, onReplayTrade }) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Build a lookup map from data.days keyed by date string
  const dayMap = useMemo(() => {
    const map = new Map<string, CalendarDay>();
    if (!data?.days || !Array.isArray(data.days)) return map;
    for (const day of data.days) {
      map.set(day.date, day);
    }
    return map;
  }, [data]);

  // Get the grid dates for the current month
  const calendarDates = useMemo(() => getCalendarDays(currentMonth), [currentMonth]);

  // Compute percentile ranks for visible days with trades in the current month
  const percentileMap = useMemo(() => {
    const pnlValues: number[] = [];
    for (const date of calendarDates) {
      if (!isSameMonth(date, currentMonth)) continue;
      const key = format(date, 'yyyy-MM-dd');
      const day = dayMap.get(key);
      if (day && day.tradeCount > 0) {
        pnlValues.push(Math.abs(day.netPnl));
      }
    }
    return computePercentileRanks(pnlValues);
  }, [calendarDates, currentMonth, dayMap]);

  // Compute monthly stats for the currently displayed month
  const monthlyStats = useMemo(() => {
    if (!data?.days || !Array.isArray(data.days)) return null;
    const monthDays = data.days.filter((d) => {
      const dayDate = new Date(d.date + 'T00:00:00');
      return isSameMonth(dayDate, currentMonth);
    });
    return computeMonthlyStats(monthDays);
  }, [data, currentMonth]);

  const now = new Date();
  const isCurrentMonth = isSameMonth(currentMonth, now);

  const handleDayClick = (dateKey: string) => {
    setSelectedDay((prev) => (prev === dateKey ? null : dateKey));
  };

  const handlePrev = () => {
    setCurrentMonth((m) => subMonths(m, 1));
    setSelectedDay(null);
  };
  const handleNext = () => {
    setCurrentMonth((m) => addMonths(m, 1));
    setSelectedDay(null);
  };

  // Loading skeleton
  if (loading && !data) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.skeleton} style={{ width: 80, height: 32 }} />
          <div className={styles.skeleton} style={{ width: 160, height: 24 }} />
          <div className={styles.skeleton} style={{ width: 80, height: 32 }} />
        </div>
        <div className={styles.grid}>
          {DAY_HEADERS.map((d) => (
            <div key={d} className={styles.dayHeader}>
              {d}
            </div>
          ))}
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className={styles.skeleton} style={{ aspectRatio: '1', minHeight: 60 }} />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.errorState}>
        <p>Failed to load calendar data</p>
        <p style={{ fontSize: '0.85rem' }}>{error}</p>
        <button className={styles.retryButton} onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  // Check if current month has any trades
  const monthHasTrades = calendarDates.some((date) => {
    if (!isSameMonth(date, currentMonth)) return false;
    const day = dayMap.get(format(date, 'yyyy-MM-dd'));
    return day && day.tradeCount > 0;
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.navButton} onClick={handlePrev}>
          &larr; Prev
        </button>
        <span className={styles.monthLabel}>{format(currentMonth, 'MMMM yyyy')}</span>
        <button className={styles.navButton} onClick={handleNext} disabled={isCurrentMonth}>
          Next &rarr;
        </button>
      </div>

      {monthlyStats && <CalendarStats stats={monthlyStats} />}

      <div className={styles.grid}>
        {DAY_HEADERS.map((d) => (
          <div key={d} className={styles.dayHeader}>
            {d}
          </div>
        ))}

        {calendarDates.map((date) => {
          const dateKey = format(date, 'yyyy-MM-dd');
          const day = dayMap.get(dateKey);
          const sameMonth = isSameMonth(date, currentMonth);
          const today = isToday(date);
          const weekend = isWeekend(date);
          const hasTrades = day && day.tradeCount > 0 && sameMonth;

          const isSelected = selectedDay === dateKey;

          // Build class list
          const cellClasses = [styles.cell];
          if (!sameMonth) cellClasses.push(styles.cellOutside);
          if (today) cellClasses.push(styles.cellToday);
          if (weekend && sameMonth && !hasTrades) cellClasses.push(styles.cellWeekend);
          if (hasTrades) cellClasses.push(styles.cellClickable);
          if (isSelected) cellClasses.push(styles.cellSelected);

          // Compute inline style for trade days
          let cellStyle: React.CSSProperties = {};
          if (hasTrades && day) {
            const absPnl = Math.abs(day.netPnl);
            const percentile = percentileMap.get(absPnl) ?? 0;
            cellStyle = pnlCellStyle(day.netPnl, percentile);
          }

          return (
            <div
              key={dateKey}
              className={cellClasses.join(' ')}
              style={cellStyle}
              onClick={hasTrades ? () => handleDayClick(dateKey) : undefined}
              role={hasTrades ? 'button' : undefined}
              tabIndex={hasTrades ? 0 : undefined}
              onKeyDown={
                hasTrades
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDayClick(dateKey);
                      }
                    }
                  : undefined
              }
            >
              <span className={styles.dateNumber}>{format(date, 'd')}</span>
              {hasTrades && day && (
                <>
                  <span className={`${styles.pnlAmount} ${day.netPnl >= 0 ? styles.pnlPositive : styles.pnlNegative}`}>
                    {day.netPnl >= 0 ? '+' : ''}${day.netPnl.toFixed(0)}
                  </span>
                  <span className={styles.badge}>{day.tradeCount}</span>
                </>
              )}
            </div>
          );
        })}
      </div>

      {selectedDay && dayMap.get(selectedDay) && (
        <DayDetail day={dayMap.get(selectedDay)!} onClose={() => setSelectedDay(null)} onReplayTrade={onReplayTrade} />
      )}

      {data && !monthHasTrades && (
        <div className={styles.emptyState}>
          <p>No trades this month</p>
        </div>
      )}
    </div>
  );
};

export default TradeCalendar;
